import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../../App';
import { updateDoc, deleteDoc, doc, onSnapshot, collection, query } from 'firebase/firestore';
import { serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../App';

function UserManagement({ users: initialUsers }) {
  const { db, userProfile } = useContext(AuthContext);
  const [users, setUsers] = useState(initialUsers || []);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userFilter, setUserFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({});
  const [loading, setLoading] = useState(false);

  // Real-time listener for users collection
  useEffect(() => {
    if (!db) return;

    const usersQuery = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(usersQuery, (snapshot) => {
      const updatedUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(updatedUsers);

      // Update selectedUser if it's currently open
      if (selectedUser) {
        const updated = updatedUsers.find(u => u.id === selectedUser.id);
        if (updated) {
          setSelectedUser(updated);
          if (!isEditing) {
            setEditedUser(updated);
          }
        }
      }
    }, (error) => {
      console.error('Error listening to users:', error);
    });

    return () => unsubscribe();
  }, [db, selectedUser?.id, isEditing]);

  // Initialize edited user when modal opens
  useEffect(() => {
    if (selectedUser) {
      setEditedUser({ ...selectedUser });
    }
  }, [selectedUser]);

  // User Management Functions
  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;

    setLoading(true);
    try {
      const updateData = {
        isDeactivated: true,
        isAccountActive: false,
        deactivatedAt: serverTimestamp()
      };

      if (userProfile?.id) {
        updateData.deactivatedBy = userProfile.id;
      }

      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('âœ… User deactivated successfully:', userId);
      alert('User deactivated successfully');
    } catch (error) {
      console.error('âŒ Error deactivating user:', error);
      alert('Failed to deactivate user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = async (userId) => {
    setLoading(true);
    try {
      const updateData = {
        isDeactivated: false,
        isAccountActive: true,
        reactivatedAt: serverTimestamp()
      };

      if (userProfile?.id) {
        updateData.reactivatedBy = userProfile.id;
      }

      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('âœ… User activated successfully:', userId);
      alert('User activated successfully');
    } catch (error) {
      console.error('âŒ Error activating user:', error);
      alert('Failed to activate user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;

    console.log('🔵 Starting user deletion for userId:', userId);
    setLoading(true);
    try {
      console.log('🔵 Functions object:', functions);
      console.log('🔵 Calling deleteUserAccount Cloud Function...');

      const deleteUserAccount = httpsCallable(functions, 'deleteUserAccount');
      console.log('🔵 Function callable created, now calling with userId:', userId);
      const result = await deleteUserAccount({ userId: userId });
      console.log('🔵 Cloud Function returned successfully:', result.data);

      console.log('âœ… User deleted successfully:', result.data);
      alert('User account deleted successfully. The user can no longer log in.');

      // Manually remove user from local state
      setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));

      setShowUserModal(false);
      setSelectedUser(null);
    } catch (error) {
      console.error('âŒ Error deleting user:', error);

      let errorMessage = 'Failed to delete user: ';
      if (error.code === 'permission-denied') {
        errorMessage += 'You do not have permission to delete users.';
      } else if (error.code === 'unauthenticated') {
        errorMessage += 'You must be logged in as super admin.';
      } else {
        errorMessage += error.message || 'Unknown error occurred';
      }

      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradeToPremium = async (userId) => {
    setLoading(true);
    try {
      const updateData = {
        membershipType: 'premium',
        upgradedAt: serverTimestamp()
      };

      if (userProfile?.id) {
        updateData.upgradedBy = userProfile.id;
      }

      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('âœ… User upgraded to premium successfully:', userId);
      alert('User upgraded to premium successfully');
    } catch (error) {
      console.error('âŒ Error upgrading user:', error);
      alert('Failed to upgrade user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDowngradeToFree = async (userId) => {
    setLoading(true);
    try {
      const updateData = {
        membershipType: 'free',
        downgradedAt: serverTimestamp()
      };

      if (userProfile?.id) {
        updateData.downgradedBy = userProfile.id;
      }

      await updateDoc(doc(db, 'users', userId), updateData);
      console.log('âœ… User downgraded to free successfully:', userId);
      alert('User downgraded to free successfully');
    } catch (error) {
      console.error('âŒ Error downgrading user:', error);
      alert('Failed to downgrade user: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserProfile = async () => {
    if (!editedUser.firstName || !editedUser.lastName || !editedUser.email) {
      alert('Please fill in all required fields (First Name, Last Name, Email)');
      return;
    }

    setLoading(true);
    try {
      // Create update object with matrimony-specific fields
      const updateData = {
        // Basic Information
        firstName: editedUser.firstName,
        lastName: editedUser.lastName,
        email: editedUser.email,
        role: editedUser.role || 'user',
        phoneNumber: editedUser.phoneNumber || '',
        dateOfBirth: editedUser.dateOfBirth || '',
        gender: editedUser.gender || '',

        // Location
        address: editedUser.address || '',
        city: editedUser.city || '',
        state: editedUser.state || '',
        zipCode: editedUser.zipCode || '',
        country: editedUser.country || '',

        // Marital Status
        maritalStatus: editedUser.maritalStatus || '',

        // Church Details
        church: editedUser.church || '',
        pastorName: editedUser.pastorName || '',
        pastorContact: editedUser.pastorContact || '',

        // Physical Appearance
        height: editedUser.height || '',
        weight: editedUser.weight || '',
        bodyType: editedUser.bodyType || '',
        complexion: editedUser.complexion || '',

        // Education & Career
        degree: editedUser.degree || '',
        occupation: editedUser.occupation || '',
        annualIncome: editedUser.annualIncome || '',

        // Family Details
        fatherName: editedUser.fatherName || '',
        fatherOccupation: editedUser.fatherOccupation || '',
        motherName: editedUser.motherName || '',
        motherOccupation: editedUser.motherOccupation || '',
        siblings: editedUser.siblings || '',

        // Religion & Caste
        religion: editedUser.religion || '',
        caste: editedUser.caste || '',
        subCaste: editedUser.subCaste || '',
        motherTongue: editedUser.motherTongue || '',

        // Horoscope
        rasi: editedUser.rasi || '',
        nakshatra: editedUser.nakshatra || '',
        gothram: editedUser.gothram || '',
        birthTime: editedUser.birthTime || '',
        birthPlace: editedUser.birthPlace || '',

        // Partner Preferences
        partnerAgeMin: editedUser.partnerAgeMin || '',
        partnerAgeMax: editedUser.partnerAgeMax || '',
        partnerHeightMin: editedUser.partnerHeightMin || '',
        partnerHeightMax: editedUser.partnerHeightMax || '',
        partnerEducation: editedUser.partnerEducation || '',
        partnerOccupation: editedUser.partnerOccupation || '',
        partnerLocation: editedUser.partnerLocation || '',
        partnerMaritalStatus: editedUser.partnerMaritalStatus || '',

        // Preferences
        language: editedUser.language || 'en',
        timezone: editedUser.timezone || '',
        emailNotifications: editedUser.emailNotifications !== undefined ? editedUser.emailNotifications : true,
        pushNotifications: editedUser.pushNotifications !== undefined ? editedUser.pushNotifications : true,
        marketingEmails: editedUser.marketingEmails !== undefined ? editedUser.marketingEmails : false,
        twoFactorEnabled: editedUser.twoFactorEnabled !== undefined ? editedUser.twoFactorEnabled : false,

        updatedAt: serverTimestamp()
      };

      if (userProfile?.id) {
        updateData.updatedBy = userProfile.id;
      }

      await updateDoc(doc(db, 'users', selectedUser.id), updateData);
      alert('User profile updated successfully');
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating user profile:', error);
      alert('Failed to update user profile: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEditChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter users based on search and filter
  const getFilteredUsers = () => {
    let filtered = users;

    if (userFilter === 'active') {
      filtered = filtered.filter(user => !user.isDeactivated && user.isAccountActive !== false);
    } else if (userFilter === 'deactivated') {
      filtered = filtered.filter(user => user.isDeactivated || user.isAccountActive === false);
    } else if (userFilter === 'premium') {
      filtered = filtered.filter(user => user.membershipType === 'premium');
    } else if (userFilter === 'free') {
      filtered = filtered.filter(user => !user.membershipType || user.membershipType === 'free');
    }

    if (userSearchTerm) {
      filtered = filtered.filter(user =>
        user.firstName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.lastName?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
        user.id?.toLowerCase().includes(userSearchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const filteredUsers = getFilteredUsers();

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-2xl shadow-xl">
        <h2 className="text-2xl font-bold mb-6 text-slate-800">User Management</h2>

        {/* Search and Filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <input
            type="text"
            placeholder="Search users by name, email, or ID..."
            value={userSearchTerm}
            onChange={(e) => setUserSearchTerm(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          />
          <select
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
          >
            <option value="all">All Users ({users.length})</option>
            <option value="active">Active Users</option>
            <option value="deactivated">Deactivated Users</option>
            <option value="premium">Premium Users</option>
            <option value="free">Free Users</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">User</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Membership</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Joined</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-8 text-gray-500">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600 flex items-center justify-center text-white font-bold mr-3">
                          {user.firstName?.[0] || user.email?.[0] || '?'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-800">
                            {user.firstName} {user.lastName}
                          </p>
                          <p className="text-xs text-gray-500">{user.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-700">{user.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.membershipType === 'premium'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-gray-100 text-gray-700'
                        }`}>
                        {user.membershipType === 'premium' ? 'Premium' : 'Free'}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${user.isDeactivated || user.isAccountActive === false
                        ? 'bg-red-100 text-red-700'
                        : 'bg-green-100 text-green-700'
                        }`}>
                        {user.isDeactivated || user.isAccountActive === false ? 'Deactivated' : 'Active'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-700">
                      {user.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowUserModal(true);
                          setIsEditing(false);
                        }}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm font-semibold"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* User Management Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-200 z-10">
              <div className="flex justify-between items-center">
                <h3 className="text-2xl font-bold text-slate-800">
                  {isEditing ? 'Edit User Profile' : 'Manage User'}
                </h3>
                <div className="flex gap-2">
                  {isEditing ? (
                    <>
                      <button
                        onClick={handleUpdateUserProfile}
                        disabled={loading}
                        className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditedUser({ ...selectedUser });
                        }}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-400 hover:bg-gray-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => setIsEditing(true)}
                      disabled={loading}
                      className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Edit Profile
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowUserModal(false);
                      setIsEditing(false);
                    }}
                    disabled={loading}
                    className="text-gray-500 hover:text-gray-700 text-2xl px-2"
                  >
                    —
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Information */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 p-6 rounded-xl border border-yellow-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      value={editedUser.firstName || ''}
                      onChange={(e) => handleEditChange('firstName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name *</label>
                    <input
                      type="text"
                      value={editedUser.lastName || ''}
                      onChange={(e) => handleEditChange('lastName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      value={editedUser.email || ''}
                      onChange={(e) => handleEditChange('email', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone Number</label>
                    <input
                      type="tel"
                      value={editedUser.phoneNumber || ''}
                      onChange={(e) => handleEditChange('phoneNumber', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Date of Birth</label>
                    <input
                      type="date"
                      value={editedUser.dateOfBirth || ''}
                      onChange={(e) => handleEditChange('dateOfBirth', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Gender</label>
                    <select
                      value={editedUser.gender || ''}
                      onChange={(e) => handleEditChange('gender', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Marital Status</label>
                    <select
                      value={editedUser.maritalStatus || ''}
                      onChange={(e) => handleEditChange('maritalStatus', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Status</option>
                      <option value="Never Married">Never Married</option>
                      <option value="Widowed">Widowed</option>
                      <option value="Divorced">Divorced</option>
                      <option value="Separated">Separated</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                    <select
                      value={editedUser.role || 'user'}
                      onChange={(e) => handleEditChange('role', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                      <option value="superadmin">Super Admin</option>
                      <option value="moderator">Moderator</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Address Information
                </h4>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Street Address</label>
                    <input
                      type="text"
                      value={editedUser.address || ''}
                      onChange={(e) => handleEditChange('address', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">City</label>
                      <input
                        type="text"
                        value={editedUser.city || ''}
                        onChange={(e) => handleEditChange('city', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">State/Province</label>
                      <input
                        type="text"
                        value={editedUser.state || ''}
                        onChange={(e) => handleEditChange('state', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Zip/Postal Code</label>
                      <input
                        type="text"
                        value={editedUser.zipCode || ''}
                        onChange={(e) => handleEditChange('zipCode', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Country</label>
                      <input
                        type="text"
                        value={editedUser.country || ''}
                        onChange={(e) => handleEditChange('country', e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Church Details */}
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Church Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Church Name</label>
                    <input
                      type="text"
                      value={editedUser.church || ''}
                      onChange={(e) => handleEditChange('church', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Pastor Name</label>
                    <input
                      type="text"
                      value={editedUser.pastorName || ''}
                      onChange={(e) => handleEditChange('pastorName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Pastor Contact</label>
                    <input
                      type="text"
                      value={editedUser.pastorContact || ''}
                      onChange={(e) => handleEditChange('pastorContact', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Physical Appearance */}
              <div className="bg-gradient-to-br from-green-50 to-teal-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Physical Appearance
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Height</label>
                    <input
                      type="text"
                      value={editedUser.height || ''}
                      onChange={(e) => handleEditChange('height', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 5'8&quot;"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Weight</label>
                    <input
                      type="text"
                      value={editedUser.weight || ''}
                      onChange={(e) => handleEditChange('weight', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 70 kg"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Body Type</label>
                    <select
                      value={editedUser.bodyType || ''}
                      onChange={(e) => handleEditChange('bodyType', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Body Type</option>
                      <option value="Slim">Slim</option>
                      <option value="Average">Average</option>
                      <option value="Athletic">Athletic</option>
                      <option value="Heavy">Heavy</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Complexion</label>
                    <select
                      value={editedUser.complexion || ''}
                      onChange={(e) => handleEditChange('complexion', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="">Select Complexion</option>
                      <option value="Fair">Fair</option>
                      <option value="Wheatish">Wheatish</option>
                      <option value="Dark">Dark</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Education & Career */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Education & Career
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Degree</label>
                    <input
                      type="text"
                      value={editedUser.degree || ''}
                      onChange={(e) => handleEditChange('degree', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., B.Tech, MBA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Occupation</label>
                    <input
                      type="text"
                      value={editedUser.occupation || ''}
                      onChange={(e) => handleEditChange('occupation', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., Software Engineer"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Annual Income</label>
                    <input
                      type="text"
                      value={editedUser.annualIncome || ''}
                      onChange={(e) => handleEditChange('annualIncome', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 10-15 Lakhs"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Family Details */}
              <div className="bg-gradient-to-br from-pink-50 to-rose-50 p-6 rounded-xl border border-pink-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Family Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Father's Name</label>
                    <input
                      type="text"
                      value={editedUser.fatherName || ''}
                      onChange={(e) => handleEditChange('fatherName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Father's Occupation</label>
                    <input
                      type="text"
                      value={editedUser.fatherOccupation || ''}
                      onChange={(e) => handleEditChange('fatherOccupation', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Mother's Name</label>
                    <input
                      type="text"
                      value={editedUser.motherName || ''}
                      onChange={(e) => handleEditChange('motherName', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Mother's Occupation</label>
                    <input
                      type="text"
                      value={editedUser.motherOccupation || ''}
                      onChange={(e) => handleEditChange('motherOccupation', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Siblings</label>
                    <input
                      type="text"
                      value={editedUser.siblings || ''}
                      onChange={(e) => handleEditChange('siblings', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 1 Brother, 1 Sister"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Religion & Caste */}
              <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-6 rounded-xl border border-indigo-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Religion & Caste
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Religion</label>
                    <input
                      type="text"
                      value={editedUser.religion || ''}
                      onChange={(e) => handleEditChange('religion', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Caste</label>
                    <input
                      type="text"
                      value={editedUser.caste || ''}
                      onChange={(e) => handleEditChange('caste', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Sub-Caste</label>
                    <input
                      type="text"
                      value={editedUser.subCaste || ''}
                      onChange={(e) => handleEditChange('subCaste', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Mother Tongue</label>
                    <input
                      type="text"
                      value={editedUser.motherTongue || ''}
                      onChange={(e) => handleEditChange('motherTongue', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Horoscope Details */}
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 p-6 rounded-xl border border-yellow-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Horoscope Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Rasi (Moon Sign)</label>
                    <input
                      type="text"
                      value={editedUser.rasi || ''}
                      onChange={(e) => handleEditChange('rasi', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Nakshatra (Star)</label>
                    <input
                      type="text"
                      value={editedUser.nakshatra || ''}
                      onChange={(e) => handleEditChange('nakshatra', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Gothram</label>
                    <input
                      type="text"
                      value={editedUser.gothram || ''}
                      onChange={(e) => handleEditChange('gothram', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Birth Time</label>
                    <input
                      type="time"
                      value={editedUser.birthTime || ''}
                      onChange={(e) => handleEditChange('birthTime', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Birth Place</label>
                    <input
                      type="text"
                      value={editedUser.birthPlace || ''}
                      onChange={(e) => handleEditChange('birthPlace', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Partner Preferences */}
              <div className="bg-gradient-to-br from-rose-50 to-red-50 p-6 rounded-xl border border-rose-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Partner Preferences
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Age Range (Min)</label>
                    <input
                      type="number"
                      value={editedUser.partnerAgeMin || ''}
                      onChange={(e) => handleEditChange('partnerAgeMin', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Min Age"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Age Range (Max)</label>
                    <input
                      type="number"
                      value={editedUser.partnerAgeMax || ''}
                      onChange={(e) => handleEditChange('partnerAgeMax', e.target.value)}
                      disabled={!isEditing}
                      placeholder="Max Age"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Height Range (Min)</label>
                    <input
                      type="text"
                      value={editedUser.partnerHeightMin || ''}
                      onChange={(e) => handleEditChange('partnerHeightMin', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 5'0&quot;"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Height Range (Max)</label>
                    <input
                      type="text"
                      value={editedUser.partnerHeightMax || ''}
                      onChange={(e) => handleEditChange('partnerHeightMax', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., 6'0&quot;"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Education Preference</label>
                    <input
                      type="text"
                      value={editedUser.partnerEducation || ''}
                      onChange={(e) => handleEditChange('partnerEducation', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., Graduate, Post Graduate"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Occupation Preference</label>
                    <input
                      type="text"
                      value={editedUser.partnerOccupation || ''}
                      onChange={(e) => handleEditChange('partnerOccupation', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., Any, Professional"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Location Preference</label>
                    <input
                      type="text"
                      value={editedUser.partnerLocation || ''}
                      onChange={(e) => handleEditChange('partnerLocation', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., India, USA"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Marital Status Preference</label>
                    <input
                      type="text"
                      value={editedUser.partnerMaritalStatus || ''}
                      onChange={(e) => handleEditChange('partnerMaritalStatus', e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., Never Married"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>
              </div>

              {/* Preferences */}
              <div className="bg-gradient-to-br from-cyan-50 to-blue-50 p-6 rounded-xl border border-cyan-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Preferences
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Language</label>
                    <select
                      value={editedUser.language || 'en'}
                      onChange={(e) => handleEditChange('language', e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    >
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="it">Italian</option>
                      <option value="pt">Portuguese</option>
                      <option value="ja">Japanese</option>
                      <option value="zh">Chinese</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Timezone</label>
                    <input
                      type="text"
                      value={editedUser.timezone || ''}
                      onChange={(e) => handleEditChange('timezone', e.target.value)}
                      disabled={!isEditing}
                      placeholder="UTC-5, America/New_York"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 disabled:bg-gray-100"
                    />
                  </div>
                </div>

                {/* Notification Preferences */}
                <div className="mt-4 space-y-3">
                  <h5 className="font-semibold text-gray-800 mb-2">Notification Settings</h5>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700 font-medium">Email Notifications</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedUser.emailNotifications !== false}
                        onChange={(e) => handleEditChange('emailNotifications', e.target.checked)}
                        disabled={!isEditing}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700 font-medium">Push Notifications</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedUser.pushNotifications !== false}
                        onChange={(e) => handleEditChange('pushNotifications', e.target.checked)}
                        disabled={!isEditing}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700 font-medium">Marketing Emails</span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedUser.marketingEmails === true}
                        onChange={(e) => handleEditChange('marketingEmails', e.target.checked)}
                        disabled={!isEditing}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Security Settings */}
              <div className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-xl border border-red-200">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Security Settings
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <div>
                      <span className="text-gray-700 font-medium">Two-Factor Authentication</span>
                      <p className="text-xs text-gray-500">Add an extra layer of security</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editedUser.twoFactorEnabled === true}
                        onChange={(e) => handleEditChange('twoFactorEnabled', e.target.checked)}
                        disabled={!isEditing}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                    </label>
                  </div>
                </div>
              </div>

              {/* Account Status & Membership */}
              <div className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-xl border border-gray-300">
                <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  Account Status & Membership
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600">User ID</p>
                    <p className="font-semibold text-gray-800 break-all">{selectedUser.id}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Membership Status</p>
                    <p className="font-semibold text-gray-800">
                      {selectedUser.membershipType === 'premium' ? 'ðŸ‘‘ Premium' : 'Free'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Account Status</p>
                    <p className={`font-semibold ${selectedUser.isDeactivated || selectedUser.isAccountActive === false
                      ? 'text-red-600'
                      : 'text-green-600'
                      }`}>
                      {selectedUser.isDeactivated || selectedUser.isAccountActive === false ? 'Deactivated' : 'Active'}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg">
                    <p className="text-sm text-gray-600">Member Since</p>
                    <p className="font-semibold text-gray-800">
                      {selectedUser.createdAt?.toDate?.().toLocaleDateString() || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-3">
                  <h5 className="font-bold text-gray-800 mb-3">Quick Actions</h5>

                  {/* Account Status Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Account Status</span>
                    {selectedUser.isDeactivated || selectedUser.isAccountActive === false ? (
                      <button
                        onClick={() => handleActivateUser(selectedUser.id)}
                        disabled={loading}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Activate Account'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleDeactivateUser(selectedUser.id)}
                        disabled={loading}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Deactivate Account'}
                      </button>
                    )}
                  </div>

                  {/* Membership Toggle */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg">
                    <span className="text-gray-700">Membership Type</span>
                    {selectedUser.membershipType === 'premium' ? (
                      <button
                        onClick={() => handleDowngradeToFree(selectedUser.id)}
                        disabled={loading}
                        className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Downgrade to Free'}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleUpgradeToPremium(selectedUser.id)}
                        disabled={loading}
                        className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Processing...' : 'Upgrade to Premium'}
                      </button>
                    )}
                  </div>

                  {/* Delete Account */}
                  <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-2 border-red-300">
                    <div>
                      <span className="text-red-700 font-semibold">Delete Account</span>
                      <p className="text-xs text-red-600">This action cannot be undone</p>
                    </div>
                    <button
                      onClick={() => handleDeleteUser(selectedUser.id)}
                      disabled={loading}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold"
                    >
                      {loading ? 'Deleting...' : 'Delete User'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Activity Log */}
              {(selectedUser.upgradedAt || selectedUser.downgradedAt || selectedUser.deactivatedAt || selectedUser.reactivatedAt || selectedUser.updatedAt) && (
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-200">
                  <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                    Activity Log
                  </h4>
                  <div className="space-y-2">
                    {selectedUser.createdAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-green-500 mr-3 text-lg"></span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Account Created</p>
                          <p className="text-sm text-gray-600">{selectedUser.createdAt.toDate?.().toLocaleString() || 'N/A'}</p>
                        </div>
                      </div>
                    )}
                    {selectedUser.updatedAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-blue-500 mr-3 text-lg"></span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Profile Updated</p>
                          <p className="text-sm text-gray-600">{selectedUser.updatedAt.toDate?.().toLocaleString() || 'N/A'}</p>
                          {selectedUser.updatedBy && (
                            <p className="text-xs text-gray-500">By: {selectedUser.updatedBy}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedUser.upgradedAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-purple-500 mr-3 text-lg"></span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Upgraded to Premium</p>
                          <p className="text-sm text-gray-600">{selectedUser.upgradedAt.toDate?.().toLocaleString() || 'N/A'}</p>
                          {selectedUser.upgradedBy && (
                            <p className="text-xs text-gray-500">By: {selectedUser.upgradedBy}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedUser.downgradedAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-gray-500 mr-3 text-lg"></span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Downgraded to Free</p>
                          <p className="text-sm text-gray-600">{selectedUser.downgradedAt.toDate?.().toLocaleString() || 'N/A'}</p>
                          {selectedUser.downgradedBy && (
                            <p className="text-xs text-gray-500">By: {selectedUser.downgradedBy}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedUser.deactivatedAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-red-500 mr-3 text-lg">ðŸš«</span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Account Deactivated</p>
                          <p className="text-sm text-gray-600">{selectedUser.deactivatedAt.toDate?.().toLocaleString() || 'N/A'}</p>
                          {selectedUser.deactivatedBy && (
                            <p className="text-xs text-gray-500">By: {selectedUser.deactivatedBy}</p>
                          )}
                        </div>
                      </div>
                    )}
                    {selectedUser.reactivatedAt && (
                      <div className="flex items-start bg-white p-3 rounded-lg">
                        {/* <span className="text-green-500 mr-3 text-lg">ðŸ”„</span> */}
                        <div>
                          <p className="font-semibold text-gray-800">Account Reactivated</p>
                          <p className="text-sm text-gray-600">{selectedUser.reactivatedAt.toDate?.().toLocaleString() || 'N/A'}</p>
                          {selectedUser.reactivatedBy && (
                            <p className="text-xs text-gray-500">By: {selectedUser.reactivatedBy}</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="sticky bottom-0 bg-white p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowUserModal(false);
                  setIsEditing(false);
                }}
                disabled={loading}
                className="w-full px-4 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManagement;
